import { DestroyRef, inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute } from '@angular/router';
import { Store } from '@ngrx/store';
import { actions, selectors } from '@twaice-fe/frontend/shared/store';
import {
  convertToDate,
  getTimeRange,
  getTzParts,
  resolveTimezone,
  tzBuild,
  tzEndOfDay,
  tzStartOfDay,
  utcEndOfDay,
  utcStartOfDay,
} from '@twaice-fe/frontend/shared/utilities';
import { DEMO_SYSTEM_DEFAULT_END_DATES } from '@twaice-fe/shared/constants';
import { CustomUserTimezone, TimeRangeInterface } from '@twaice-fe/shared/models';
import { getQueryParamAsDate, TimeLength, TimeRangeEnum, TimeRangeEnumDE } from '@twaice-fe/shared/utilities';
import { subDays } from 'date-fns';
import { combineLatest, distinctUntilChanged, filter, map, Observable, withLatestFrom } from 'rxjs';

const { systemActions } = actions;
const { systemSelectors, authSelectors } = selectors;

type TimeRangeConfig = Record<string, Date>;

export interface DemoTimeRange {
  timeRange: TimeRangeInterface | undefined;
  availableTimeRanges: TimeRangeInterface[];
  isUserSelectedTimeRange: boolean;
}

/** Everything the resolution depends on, gathered from the store and the route. */
interface TimeRangeInputs {
  systemId: string;
  defaultTimeRange: TimeRangeEnum | TimeRangeEnumDE | undefined;
  queryParams: Record<string, string>;
  selectedTimeRange: TimeRangeInterface | undefined;
  isUserSelectedTimeRange: boolean | undefined;
  /** The timezone the ranges are expressed in; undefined means UTC. */
  timezone: string | undefined;
}

interface Range {
  disabled: boolean;
  id: string;
  label: string;
  value: [Date, Date];
}

/* eslint-disable @typescript-eslint/naming-convention */
const timeRangeIdLookup: Record<number, string> = {
  7: 'sevenDays',
  30: 'thirtyDays',
  60: 'sixtyDays',
  90: 'ninetyDays',
  365: 'threeHundredSixtyFiveDays',
};
/* eslint-enable @typescript-eslint/naming-convention */

/**
 * This service overrides the default time range behavior for demo systems.
 * By including this service we can set specific selected dates by default,
 * with a per-feature system/date mapping.
 *
 * Data Explorer resolves its time ranges relative to now rather than to a pinned
 * demo end date, so it does not use this service — see DataExplorerTimeRangeService.
 */
@Injectable({
  providedIn: 'root',
})
export class DemoTimeRangeService {
  destroyRef = inject(DestroyRef);

  constructor(
    private store: Store,
    private route: ActivatedRoute
  ) {}

  /**
   * The time range this page should show, and the ranges it can offer, recalculated
   * whenever the selected system, the stored default or the timezone changes.
   *
   * Reads only — call `syncTimeRange` to apply the result to the store.
   */
  watchTimeRange(timeRangeConfig?: TimeRangeConfig): Observable<DemoTimeRange> {
    return this.watchTimeRangeInputs().pipe(map((inputs) => this.resolveTimeRange(inputs, timeRangeConfig)));
  }

  /**
   * Keeps the store's time range in step with `watchTimeRange` until `destroyRef` is destroyed.
   * Call this once, from the page that owns the time range.
   */
  syncTimeRange(destroyRef: DestroyRef, timeRangeConfig?: TimeRangeConfig): void {
    this.watchTimeRange(timeRangeConfig)
      .pipe(takeUntilDestroyed(destroyRef))
      .subscribe((demoTimeRange) => this.publishTimeRange(demoTimeRange));
  }

  /**
   * For features that hold their time range outside the global store.
   * If the time range comes from the global store, use `syncTimeRange` instead.
   */
  getTimeRangeFromRoute(systemId: string, timeRangeConfig?: TimeRangeConfig) {
    // If it is not pre-set with input variable, we set the time-range to the query value or to the last hour
    const defaultStartDate =
      timeRangeConfig?.[systemId] ?? DEMO_SYSTEM_DEFAULT_END_DATES[systemId] ?? new Date(new Date().setHours(0, 0, 0, 0));
    const from = this.route.snapshot.queryParams['from']
      ? getQueryParamAsDate(this.route.snapshot.queryParams, 'from')
      : new Date(defaultStartDate.getTime() - TimeLength.DAY * 6);

    const to = this.route.snapshot.queryParams['to']
      ? getQueryParamAsDate(this.route.snapshot.queryParams, 'to')
      : defaultStartDate;

    return { from, to };
  }

  private getTimeRangeInterfaceFromRoute(
    queryParams: Record<string, string>,
    timezone: string | undefined
  ): TimeRangeInterface | undefined {
    const { startDate, endDate, timeRange: id } = queryParams;

    if (!id || !(startDate && endDate)) {
      return undefined;
    }

    if (id === 'custom') {
      return {
        id,
        value: [convertToDate(Number(startDate)), convertToDate(Number(endDate))],
        label: 'Custom',
        disabled: true,
      };
    }

    return this.getAvailableTimeRanges(undefined, '', timezone).find((range) => range.id === id);
  }

  private getAvailableTimeRanges(
    timeRangeConfig: TimeRangeConfig | undefined,
    systemId: string,
    timezone: string | undefined
  ): TimeRangeInterface[] {
    const now = new Date();
    const referenceEndDate = timezone ? tzEndOfDay(now, timezone) : utcEndOfDay(now);
    const systemEndDate = timeRangeConfig?.[systemId] ?? DEMO_SYSTEM_DEFAULT_END_DATES[systemId] ?? referenceEndDate;
    return this.getTimeRanges(systemEndDate, timezone);
  }

  /** Emits whenever anything that can change the resolved time range changes. */
  private watchTimeRangeInputs(): Observable<TimeRangeInputs> {
    const selectedSystem$ = this.store.select(systemSelectors.getSelected).pipe(
      filter((system) => !!system),
      distinctUntilChanged(
        (prev, curr) =>
          prev.systemBk === curr.systemBk && prev.bessMetadata?.storageTimezone === curr.bessMetadata?.storageTimezone
      )
    );
    const defaultTimeRange$ = this.store
      .select(systemSelectors.getDefaultTimeRange)
      .pipe(distinctUntilChanged((prev, curr) => prev === curr));
    const userTimezone$ = this.store.select(authSelectors.getUserTimezone).pipe(distinctUntilChanged());

    return combineLatest([selectedSystem$, defaultTimeRange$, userTimezone$]).pipe(
      takeUntilDestroyed(this.destroyRef),
      withLatestFrom(
        this.route.queryParams,
        this.store.select(systemSelectors.getTimeRange),
        this.store.select(systemSelectors.getIsUserSelectedTimeRange)
      ),
      map(([[system, defaultTimeRange], queryParams, selectedTimeRange, isUserSelectedTimeRange]) => ({
        systemId: system.id,
        defaultTimeRange,
        queryParams,
        selectedTimeRange,
        isUserSelectedTimeRange,
        timezone: resolveTimezone(CustomUserTimezone.STORAGE_TIME, system.bessMetadata?.storageTimezone || null) ?? undefined,
      }))
    );
  }

  /** Decides which range the page should show, and which ranges it can offer. */
  private resolveTimeRange(
    { systemId, defaultTimeRange, queryParams, selectedTimeRange, isUserSelectedTimeRange, timezone }: TimeRangeInputs,
    timeRangeConfig: TimeRangeConfig | undefined
  ): DemoTimeRange {
    const timeRangeFromQueryParams = this.getTimeRangeInterfaceFromRoute(queryParams, timezone);
    const availableTimeRanges = this.getAvailableTimeRanges(timeRangeConfig, systemId, timezone);
    const defaultRange = this.resolveDefaultTimeRange(defaultTimeRange, availableTimeRanges, timeRangeFromQueryParams);

    const chosenRange = this.hasFixedEndDate(systemId, timeRangeConfig)
      ? defaultRange
      : this.resolveUserSelectedTimeRange(selectedTimeRange, timeRangeFromQueryParams, isUserSelectedTimeRange) ?? defaultRange;

    return {
      // A range carried over from elsewhere keeps its old dates, so re-read it from the
      // current list. The query params are already resolved against that list.
      timeRange: timeRangeFromQueryParams ? chosenRange : this.matchToAvailableTimeRanges(chosenRange, availableTimeRanges),
      availableTimeRanges,
      isUserSelectedTimeRange: isUserSelectedTimeRange || !!timeRangeFromQueryParams,
    };
  }

  /** A system pinned to a fixed end date always shows its default range, whatever the user picked. */
  private hasFixedEndDate(systemId: string, timeRangeConfig: TimeRangeConfig | undefined): boolean {
    return !!(timeRangeConfig?.[systemId] ?? DEMO_SYSTEM_DEFAULT_END_DATES[systemId]);
  }

  private resolveDefaultTimeRange(
    defaultTimeRange: TimeRangeEnum | TimeRangeEnumDE | undefined,
    availableTimeRanges: TimeRangeInterface[],
    timeRangeFromQueryParams: TimeRangeInterface | undefined
  ): TimeRangeInterface | undefined {
    return getTimeRange(defaultTimeRange, TimeRangeEnum.LAST_THIRTY_DAYS, availableTimeRanges, timeRangeFromQueryParams);
  }

  private resolveUserSelectedTimeRange(
    selectedTimeRange: TimeRangeInterface | undefined,
    timeRangeFromQueryParams: TimeRangeInterface | undefined,
    incomingTimeRangeIsUserSelected: boolean | undefined
  ): TimeRangeInterface | undefined {
    // always prioritize the time range from the query params, if that is not set, check if the incoming
    // timerange is user selected or default and only return it if it is user selected
    return timeRangeFromQueryParams ?? (incomingTimeRangeIsUserSelected ? selectedTimeRange : undefined);
  }

  /** Swaps a range for the entry with the same id in the current list, so its dates are up to date. */
  private matchToAvailableTimeRanges(
    timeRange: TimeRangeInterface | undefined,
    availableTimeRanges: TimeRangeInterface[]
  ): TimeRangeInterface | undefined {
    if (!timeRange?.id || timeRange.id === 'custom') {
      return timeRange;
    }

    return availableTimeRanges.find((range) => range.id === timeRange.id) ?? timeRange;
  }

  private publishTimeRange({ timeRange, availableTimeRanges, isUserSelectedTimeRange }: DemoTimeRange): void {
    if (!timeRange) {
      return;
    }

    this.store.dispatch(systemActions.setInitialTimeRange({ timeRange, availableTimeRanges, isUserSelectedTimeRange }));
  }

  private getTimeRanges(systemEndDate: Date, timezone: string | undefined): TimeRangeInterface[] {
    const dateDeltas = [7, 30, 60, 90, 365];

    return dateDeltas.map((delta) => this.getTimeRangesForPage(delta, systemEndDate, timezone));
  }

  private getTimeRangesForPage(delta: number, systemEndDate: Date, tz: string | undefined): Range {
    let startDate: Date;
    let endDate: Date;

    if (tz) {
      // Subtract calendar days in the selected TZ to account for DST
      const endParts = getTzParts(systemEndDate, tz);
      const startRef = tzBuild(
        endParts.year,
        endParts.month,
        endParts.day - (delta - 1),
        endParts.hour,
        endParts.minute,
        endParts.second,
        0,
        tz
      );
      endDate = tzEndOfDay(systemEndDate, tz);
      startDate = tzStartOfDay(startRef, tz);
    } else {
      startDate = utcStartOfDay(subDays(systemEndDate, delta - 1));
      endDate = utcEndOfDay(systemEndDate);
    }

    return {
      id: timeRangeIdLookup[delta] ?? null,
      value: [startDate, endDate],
      label: `Last ${delta} days`,
      disabled: false,
    };
  }
}
